import {
  ChevronDownIcon,
  CloseIcon,
  EditIcon,
  SearchIcon,
  ViewIcon,
  DeleteIcon,
  WarningTwoIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  DrawerBody,
  DrawerHeader,
  Flex,
  HStack,
  Heading,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup, InputRightElement,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import QrCodePrint from "components/AppQrCodePdfPrint";
import AppSelect from "components/AppSelect";
import AppViewDrawer from "components/AppViewDrawer";
import AppViewListItem from "components/AppViewListItem";
import LoadingModal from "components/LoadingModal";
import { Pagination } from "components/Pagination";
import { useApi } from "hooks/useApi";
import { useAuthentication } from "hooks/useAuthentication";
import { useCache } from "hooks/useCache";
import { PrinterIcon } from "lucide-react";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MdMap, MdOutlinePayment } from "react-icons/md";
import { usePaystackPayment } from "react-paystack";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { Store } from "store";
import { closeLoader, openLoader, showToast, toastTypes } from "utils";
import { useUserRole } from "hooks/useUserRole";
import { TAX_COLLECTOR, TAX_SUPERVISOR, TAX_SUPER_ADMIN } from "utils/roles";
import { ReactTable } from "../../components/ReactTable";

type Table3RowType = {
  id: number;
  tax_identification_number: string;
  company_name: string;
  name: string;
  rep_name: string;
  email: string;
  area_name: string;
  sub_area_name: string;
  phone_number: string;
  actionHeader: undefined;
  profile_picture: string;
  country_dial_code: string;
  lat: string;
  long: string;
  is_active: boolean;
  tax_is_due: boolean;
  payment_api_pk: string;
  assessment_reference: string;
  amount_due: number;
  due_date: string;
  qr_codes: string;
  tax_nature: string;
};

export default function TaxPayerManagementSection() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const cancelRef = useRef<any>();
  const [activeItem, setActiveItem] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const loadingRef = useRef<any>(null);
  const payment_reference = useMemo(() => new Date().getTime().toString(), []);
  const toast = useToast();
  const navigate = useNavigate();
  const { state: { user }, dispatch } = useContext(Store);
  const { hasPermission } = useAuthentication();
  const userRole = useUserRole();
  const [subArea, setSubArea] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [institution, setInstitution] = useState<string>("");
  const [taxNature, setTaxNature] = useState<string>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchBarValue34, setSearchBarValue34] = useState("");
  const {
    getTaxPayers,
    updateTaxPayer,
    getInstitutions,
    getCities,
    getSubAreas,
    verifyPayment,
    getTaxNatures,
    deleteTaxPayer,
  } = useApi();
  const {
    data: taxPayers,
    mutate,
    isLoading: isTaxPayerLoading,
  } = useCache("taxPayers", () =>
    getTaxPayers(page, searchBarValue34, 50, "", institution, city, subArea, taxNature)
  );
  const { data: taxNatures, isLoading:isTaxNatureLoading } = useCache("taxNatures", () => getTaxNatures());
  const {
    data: institutions,
    isLoading: isInstitutionLoading,
    mutate: mutateInstitution,
  } = useCache("institutions", () => getInstitutions(1, "", ""));
  const {
    data: subAreas,
    isLoading: isSubAreaLoading,
    mutate: mutateSubArea,
  } = useCache("subAreas", () => getSubAreas(1, "", "", institution));
  const {
    data: cities,
    isLoading: isCityLoading,
    mutate: mutateCity,
  } = useCache("cities", () => getCities("", 1, "", ""));
  const handleSearch = (event: any) => {
    const value = event.target.value;
    mutate(getTaxPayers(page, value));
  };
  const handleClearSearch = (event: any) => {
    setSearchBarValue34("");
    mutate(getTaxPayers(page, ""));
  };

  const handleJumpToPage = (pg: number) => {
    setPage(pg);
    mutate(getTaxPayers(pg));
  };
  const handleSetNextPage = () => {
    if (taxPayers.length > 0 && taxPayers[0].num_of_pages >= page + 1) {
      setPage((prev: any) => prev + 1);
      mutate(getTaxPayers(page + 1));
    }
  };
  const handleSetPrevPage = async () => {
    if (taxPayers.length > 0 && page > 0) {
      setPage((prev: any) => prev - 1);
    }
  };

  const handleViewItem = useCallback(
    (item: any) => {
      setActiveItem(item);
      onOpen();
    },
    [onOpen, setActiveItem]
  );

  const handleUpdateActive = useCallback(
    async (taxPayer: any) => {
      // const postData = {...client, address:{...client?.address}, is_active: client?.is_active ? false : true}
      let formData: any = new FormData();
      formData.append("first_name", taxPayer.first_name);
      formData.append("company_name", taxPayer.company_name);
      formData.append("last_name", taxPayer.last_name);
      formData.append("phone_number", taxPayer.phone_number);
      formData.append("country_id", taxPayer.country_id);
      formData.append("email", taxPayer.email);
      formData.append("lat", taxPayer.lat);
      formData.append("long", taxPayer.long);
      formData.append("description", taxPayer.description);
      formData.append("city_id", taxPayer.city_id);
      formData.append("sub_area_id", taxPayer.sub_area_id);
      formData.append("landmark", taxPayer.landmark);
      formData.append("institution_id", taxPayer.institution_id);
      formData.append("id", taxPayer.id);
      formData.append("is_active", taxPayer?.is_active ? false : true);
      const response = await updateTaxPayer(formData);
      if (response.id) {
        dispatch(
          showToast({
            toast: toast,
            message: "Le Débiteur a été mis à jour avec succès.",
            status: toastTypes.success,
          })
        );
        mutate(getTaxPayers(page, searchBarValue34));
      }
    },
    [
      dispatch,
      getTaxPayers,
      mutate,
      page,
      searchBarValue34,
      toast,
      updateTaxPayer,
    ]
  );

  useEffect(() => {
    if (isTaxPayerLoading) {
      openLoader(loadingRef);
    } else {
      closeLoader(loadingRef);
    }
  }, [isTaxPayerLoading]);

  const config: any = () => {
    return {
      publicKey: user?.user?.payment_api_pk as string,
    };
  };
  const initializePayment: any = usePaystackPayment(config());
  const handlePayment = useCallback(
    async (response: any, assessment_reference: string, due_date:string) => {
      openLoader(loadingRef);
      const { reference } = response;
      if (response.reference) {
        const verifyResponse = await verifyPayment({
          reference: reference,
          assessment_reference: assessment_reference,
        });
        if (verifyResponse.id) {
          mutate(getTaxPayers(page, searchBarValue34));
          dispatch(
            showToast({
              toast: toast,
              message:
                `Le règlement de l’échéance du (${due_date}) a été réalisé avec succès. Votre reçu vous a été envoyé par courrier électronique`,
              status: toastTypes.success,
            })
          );
          closeLoader(loadingRef);
        } else {
          closeLoader(loadingRef);
          dispatch(
            showToast({
              toast: toast,
              message: "Le paiement n'a pas été effectué.",
              status: toastTypes.error,
            })
          );
          // take them to booking detail page to make or verify payment
        }
      } else {
        closeLoader(loadingRef);
        dispatch(
          showToast({
            toast: toast,
            message:
              "Impossible d'effectuer le paiement pour le moment, veuillez réessayer plus tar",
            status: toastTypes.error,
          })
        );
      }
    },
    [
      verifyPayment,
      dispatch,
      toast,
      getTaxPayers,
      mutate,
      page,
      searchBarValue34,
    ]
  );
  const onSuccess = useCallback(
    (response: any, assessment_reference: string, due_date:string) => {
      handlePayment(response, assessment_reference, due_date);
    },
    [handlePayment]
  );

  const onPaymentClose = useCallback(() => {
    closeLoader(loadingRef);
  }, [loadingRef]);


  const table3Columns = useMemo(() => {
    const table3ColumnHelper = createColumnHelper<Table3RowType>();
    return [
      table3ColumnHelper.accessor("company_name", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Image
                src={
                  (info.row.original.profile_picture as unknown as string) ||
                  "/images/dummy.png"
                }
                fallbackSrc="/images/dummy.png"
                alt="Débiteur Profile Image"
                h="32px"
                w="32px"
                fit="contain"
                borderRadius="10px"
              />
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Débiteur</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("rep_name", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Nom du représentant</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("area_name", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Commune</Heading>
          </Flex>
        ),
        meta: { width: "90px" },
      }),
      table3ColumnHelper.accessor("sub_area_name", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Zone</Heading>
          </Flex>
        ),
        meta: { width: "90px" },
      }),
      table3ColumnHelper.accessor("email", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Email</Heading>
          </Flex>
        ),
        meta: { width: "132px" },
      }),
      table3ColumnHelper.accessor("phone_number", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Nro de telephone</Heading>
          </Flex>
        ),
        meta: { width: "170px" },
      }),
      table3ColumnHelper.accessor("is_active", {
        cell: (info) => (
          <Flex justifyContent="flex-start" width={"100px"}>
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                size={"sm"}
                colorScheme={
                  info.getValue<boolean>() ? "green_360" : "red_origin"
                }
                borderRadius={"4px"}
                px={"5px"}
                fontSize={"10px"}
              >
                {info.getValue<boolean>() ? "Actif" : "Inactif"}
              </MenuButton>
              <MenuList>
                <MenuItem onClick={() => handleUpdateActive(info.row.original)}>
                  <span>
                    {info.getValue<boolean>() ? "Désactiver" : "Activer"}
                  </span>
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        ),
        header: (info) => (
          <Flex px="15px" py="14px" justifyContent={"flex-start"}>
            <Heading as="h6">État</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
      table3ColumnHelper.accessor("id", {
        cell: (info) => (
          <HStack gap={"2px"} width={"100%"} justifyContent={"flex-end"}>
            {hasPermission("taxpayer", "update") && (
              <IconButton
                variant="outline"
                colorScheme="gray_stroke_0"
                aria-label="Edit"
                size={"sm"}
                icon={<EditIcon />}
                borderRadius={"4px"}
                onClick={() => navigate(`edit/${info.getValue()}`)}
              />
            )}
            {hasPermission("taxpayer", "get") && (
              <IconButton
                variant="outline"
                colorScheme="gray_stroke_0"
                aria-label="view"
                size={"sm"}
                icon={<ViewIcon />}
                borderRadius={"4px"}
                onClick={() => handleViewItem(info.row.original)}
              />
            )}
            {info.row.original?.tax_is_due &&
              (userRole === TAX_COLLECTOR || userRole === TAX_SUPERVISOR) && (
                <IconButton
                  variant="outline"
                  colorScheme="gray_stroke_0"
                  aria-label="view"
                  size={"sm"}
                  icon={<Icon as={MdOutlinePayment as any} boxSize={4} />}
                  borderRadius={"4px"}
                  onClick={() => {
                    const taxPayer = info.row.original;
                    setActiveItem(taxPayer)
                    const dynamicConfig = {
                      publicKey: taxPayer?.payment_api_pk as string,
                      currency: process.env
                        .REACT_APP_PAYSTACK_CURRENCY as string,
                      reference: payment_reference,
                      email: taxPayer?.email,
                      firstname: taxPayer?.name.split(" ")[1],
                      lastname: taxPayer?.name.split(" ")[0],
                      phone: taxPayer?.phone_number,
                      amount: taxPayer?.amount_due * 100, //Amount is in the country's lowest currency. E.g Kobo, so 20000 kobo = N200
                      metadata: {
                        assessment_reference: taxPayer?.assessment_reference,
                      },
                    };
                    initializePayment({
                      onSuccess: (rspnse: any) => {
                        onSuccess(rspnse, taxPayer?.assessment_reference,taxPayer.due_date);
                      },
                      onClose: onPaymentClose,
                      config: dynamicConfig,
                    });
                  }}
                />
              )}
            {hasPermission("taxpayer", "delete") && (
              <IconButton
                variant="outline"
                colorScheme="red_origin"
                aria-label="delete"
                size={"sm"}
                icon={<DeleteIcon />}
                borderRadius={"4px"}
                onClick={() => {
                  setActiveItem(info.row.original);
                  onConfirmOpen();
                }}
                isDisabled={isDeleting && activeItem?.id === info.row.original.id}
                isLoading={isDeleting && activeItem?.id === info.row.original.id}
              />
            )}
            <Link
              href={`https://www.google.com/maps/search/?api=1&query=${info.row.original.lat},${info.row.original.long}`}
              target="_blank"
            >
              <IconButton
                variant="outline"
                colorScheme="gray_stroke_0"
                aria-label="view"
                size={"sm"}
                icon={<Icon as={MdMap as any} boxSize={4} />}
                borderRadius={"4px"}
                onClick={() => handleViewItem(info.row.original)}
              />
            </Link>
          </HStack>
        ),
        header: (info) => (
          <Flex width={"100%"} justifyContent={"flex-end"} px="15px" py="14px">
            <Heading as="h6">Action</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
    ];
  }, [
    navigate,
    handleViewItem,
    hasPermission,
    handleUpdateActive,
    initializePayment,
    onPaymentClose,
    onSuccess,
    payment_reference,
    user,
    isDeleting,
    activeItem,
    onConfirmOpen,
  ]);

  const componentRef = useRef<any>();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  return (
    <>
      <Flex
        flexDirection="column"
        alignItems="start"
        px={{ base: "20px", sm: "24px" }}
        minHeight={"100vh"}
      >
        <Flex gap="4px" alignSelf="stretch" flexDirection="column">
          <Flex
            justifyContent="center"
            alignItems="center"
            flexDirection={{ base: "column", sm: "row" }}
          >
            <Heading size="text2xl" as="h5" fontFamily="Arimo">
              Débiteurs
            </Heading>
            <Flex
              pl="56px"
              pr="130px"
              gap="13px"
              flex={1}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf={{ base: "stretch", sm: "auto" }}
              flexDirection={{ base: "column", sm: "row" }}
              px={{ md: 0, base: "20px" }}
            >
              {hasPermission("taxpayer", "create") && (
                <HStack>
                  <Button
                    size="xl"
                    variant="outline"
                    colorScheme="red_origin"
                    leftIcon={
                      <Image
                        src="images/img_handshake_deal_svgrepo_com_red_origin.svg"
                        alt="Handshake-deal Svgrepo.com"
                      />
                    }
                    fontWeight={700}
                    gap="8px"
                    minW="128px"
                    borderRadius="8px"
                    as={Link}
                    href="/tax-payers/add/upload"
                  >
                    Charger les données des Débiteurs
                  </Button>
                  <Button
                    size="xl"
                    variant="outline"
                    colorScheme="red_origin"
                    leftIcon={
                      <Image
                        src="images/img_handshake_deal_svgrepo_com_red_origin.svg"
                        alt="Handshake-deal Svgrepo.com"
                      />
                    }
                    fontWeight={700}
                    gap="8px"
                    minW="128px"
                    borderRadius="8px"
                    as={Link}
                    href="/tax-payers/add"
                  >
                    Ajouter un Débiteur
                  </Button>
                </HStack>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Box
          mt="16px"
          bg="white"
          w={{ md: "100%", base: "100%" }}
          borderRadius="10px"
        >
          <Flex
            bg="white"
            justifyContent="center"
            alignItems="center"
            px="12px"
            py="16px"
            flexDirection={{ md: "row", base: "column" }}
          >
            <Flex
              flex={1}
              alignItems="center"
              alignSelf={{ md: "auto", base: "stretch" }}
              flexDirection={{ base: "column", sm: "row" }}
              gap={2}
            >
              {!isInstitutionLoading &&
                userRole === TAX_SUPER_ADMIN && (
                  <AppSelect
                    height={"40px"}
                    id="institution"
                    name="institution"
                    onChange={(field: string, value: any) => {
                      setInstitution(value);
                      setSubArea(""); // Réinitialiser la commune quand l'institution change
                      mutateSubArea(getSubAreas(1, "", "", value)); // Recharger les communes pour la nouvelle institution
                      mutate(
                        getTaxPayers(
                          page,
                          searchBarValue34,
                          50,
                          "",
                          value,
                          city,
                          "",
                          taxNature
                        )
                      );
                    }}
                    value={institution as string}
                    placeholder="Veuillez sélectionner une institution"
                    options={institutions}
                    isMulti={false}
                    isLoading={isInstitutionLoading}
                  />
                )}
              {!isCityLoading && userRole === TAX_SUPER_ADMIN && (
                <AppSelect
                  height={"40px"}
                  id="city"
                  name="city"
                  onChange={(field: string, value: any) => {
                    setCity(value);
                    mutate(
                      getTaxPayers(
                        page,
                        searchBarValue34,
                        50,
                        "",
                        institution,
                        value,
                        subArea,
                        taxNature
                      )
                    );
                  }}
                  value={city as string}
                  placeholder="Veuillez sélectionner une ville"
                  options={cities}
                  isMulti={false}
                  isLoading={isCityLoading}
                />
              )}
              {!isSubAreaLoading && userRole === TAX_SUPER_ADMIN && (
                <AppSelect
                  height={"40px"}
                  id="subArea"
                  name="subArea"
                  onChange={(field: string, value: any) => {
                    setSubArea(value);
                    mutate(
                      getTaxPayers(
                        page,
                        searchBarValue34,
                        50,
                        "",
                        institution,
                        city,
                        value,
                        taxNature
                      )
                    );
                  }}
                  value={subArea as string}
                  placeholder="Veuillez sélectionner une commune"
                  options={subAreas}
                  isMulti={false}
                  isLoading={isSubAreaLoading}
                />
              )}
              {!isTaxNatureLoading && userRole === TAX_SUPER_ADMIN && (
                <AppSelect
                  height={"40px"}
                  id="taxNature"
                  name="taxNature"
                  onChange={(field: string, value: any) => {
                    if (!value) {
                      mutate(
                        getTaxPayers(
                          page,
                          searchBarValue34,
                          50,
                          "",
                          institution,
                          city,
                          subArea,
                          value
                        )
                      );
                    }
                    setTaxNature(value);
                  }}
                  value={taxNature as string}
                  placeholder="Veuillez sélectionner nature de règlement"
                  options={taxNatures}
                  isMulti={false}
                  isLoading={isTaxNatureLoading}
                />
              )}
              <InputGroup w={{ base: "100%", sm: "100%" }}>
                
                <Input
                  placeholder={`Rechercher un Débiteur`}
                  value={searchBarValue34}
                  onChange={(event: any) =>
                    setSearchBarValue34(event.currentTarget.value)
                  }
                  gap="8px"
                  borderWidth="1px"
                  borderRadius="8px"
                />
                <InputRightElement>
                  {searchBarValue34?.length > 0 ? (
                    <CloseIcon onClick={handleClearSearch} />
                  ) : null}
                </InputRightElement>
              </InputGroup>
              <IconButton
                onClick={handleSearch}
                ml={2}
                borderRadius={"10px"}
                py={"19px"}
                border={"1px solid #e2e4e6"}
                aria-label="search"
                icon={<SearchIcon />}
                justifyContent={"center"}
                alignItems={"center"}
              />
            </Flex>
          </Flex>
          <ReactTable
            size="sm"
            headerCellProps={{ bg: "white" }}
            tableProps={{
              borderColor: "neutral.600",
              borderTopWidth: "1px",
              borderStyle: "solid",
            }}
            columns={table3Columns as any}
            data={taxPayers}
          />
          <Pagination
            pageIndex={page}
            pageSize={pageSize}
            setNextPageIndex={handleSetNextPage}
            setPrevPageIndex={handleSetPrevPage}
            setPageSize={setPageSize}
            jumpToPage={handleJumpToPage}
            numberOfPages={
              taxPayers && taxPayers.length > 0 && taxPayers[0].num_of_pages
            }
          />
        </Box>
      </Flex>
      <AppViewDrawer isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <DrawerHeader>Fiche Débiteur</DrawerHeader>

        <DrawerBody>
          <VStack alignItems={"flex-start"} gap={"15px"} width={"100%"}>
            {/* Première ligne : Boutons Modifier et Supprimer */}
            <HStack justifyContent={"space-between"} width={"100%"} gap={"10px"}>
              {hasPermission("taxpayer", "update") && <Button
                size="xl"
                variant="outline"
                colorScheme="green_360"
                leftIcon={<EditIcon />}
                fontWeight={700}
                gap="2px"
                borderRadius="8px"
                flex={1}
                as={Link}
                href={`/tax-payers/edit/${activeItem?.id}`}
              >
                Modifier
              </Button>}
              {hasPermission("taxpayer", "delete") && (
                <Button
                  size="xl"
                  variant="outline"
                  colorScheme="red_origin"
                  leftIcon={<DeleteIcon />}
                  fontWeight={700}
                  gap="8px"
                  borderRadius="8px"
                  flex={1}
                  onClick={() => {
                    onConfirmOpen();
                  }}
                  isDisabled={isDeleting}
                  isLoading={isDeleting}
                >
                  Supprimer
                </Button>
              )}
            </HStack>
            {/* Deuxième ligne : Bouton Afficher le code QR */}
            <Button
              size="xl"
              w={"100%"}
              colorScheme="gray_description"
              fontWeight={700}
              boxShadow="md"
              borderRadius="10px"
              type="submit"
              leftIcon={<PrinterIcon />}
              onClick={handlePrint}
            >
              Afficher le code QR
            </Button>
            <VStack alignItems={"center"} gap={"5px"} width={"100%"}>
              <Flex gap="8px" padding={"1px"}>
                <Image
                  src={activeItem?.profile_picture}
                  fallbackSrc="/images/dummy.png"
                  alt="logo Image"
                  h="100px"
                  w="100px"
                  fit="contain"
                  borderRadius="16px"
                />
              </Flex>
              <Box display={"none"}>
                <QrCodePrint
                  value={activeItem?.tax_identification_number}
                  ref={componentRef}
                />
              </Box>
            </VStack>
            <AppViewListItem
              label="Numéro d'identification"
              value={activeItem?.tax_identification_number}
            />
            <AppViewListItem label="Nom du débiteur" value={activeItem?.name} />
            <AppViewListItem label="Email" value={activeItem?.email} />
            <AppViewListItem
              label="Numéro de téléphone"
              value={`${activeItem?.phone_number}`}
            />
            <AppViewListItem label="Commune" value={activeItem?.city_name} />
            <AppViewListItem label="Zone" value={activeItem?.sub_area_name} />
            <AppViewListItem label="Latitude" value={activeItem?.lat} />
            <AppViewListItem label="Longitude" value={activeItem?.long} />
            <AppViewListItem
              label="Addresse Description"
              value={activeItem?.description}
            />
            <AppViewListItem
              label="Point de repère"
              value={activeItem?.landmark}
            />
            <AppViewListItem
              label="Institution"
              value={activeItem?.institution_name}
            />
          </VStack>
        </DrawerBody>
      </AppViewDrawer>
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px" boxShadow="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" display="flex" alignItems="center" gap={2}>
              <WarningTwoIcon color="red.400" />
              Confirmation de suppression
            </AlertDialogHeader>

            <AlertDialogBody fontSize="xl" color="gray.700" textAlign="center">
              <VStack spacing={3} align="center">
                <WarningTwoIcon boxSize={16} color="red.400" />
                <Text fontSize="xl">
                  Voulez-vous vraiment supprimer le contribuable « {activeItem?.company_name || activeItem?.name} » ?
                </Text>
                <Text fontSize="xl">
                  Cette action est irréversible.
                </Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose} variant="outline" colorScheme="gray_stroke_0" leftIcon={<CloseIcon boxSize={3} />} borderRadius="8px" isDisabled={isDeleting}>
                Annuler
              </Button>
              <Button colorScheme="red_origin" ml={3} leftIcon={<DeleteIcon />} borderRadius="8px" isLoading={isDeleting} loadingText="Suppression..." onClick={async () => {
                if (!activeItem?.id) return;
                try {
                  setIsDeleting(true);
                  await deleteTaxPayer(activeItem.id);
                  dispatch(
                    showToast({
                      toast,
                      message: "Contribuable supprimé avec succès",
                      status: toastTypes.success,
                    })
                  );
                  onConfirmClose();
                  onClose();
                  mutate(getTaxPayers(page, searchBarValue34));
                } finally {
                  setIsDeleting(false);
                }
              }}>
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <LoadingModal ref={loadingRef} />
    </>
  );
}
